import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Zap,
  TrendingUp,
  Sparkles,
  Brain,
  Layers,
  ShieldAlert,
  Activity,
  Database,
  Bot,
  Lock,
  Boxes,
  Cpu,
} from "lucide-react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { SEO } from "@/components/SEO";

/* ───────────────────────────── Data ───────────────────────────── */
const featurePills = [
  { Icon: Brain, label: "Predictive AI Models" },
  { Icon: ShieldAlert, label: "Fraud Detection" },
  { Icon: TrendingUp, label: "Persistency Analytics" },
  { Icon: Activity, label: "Customer Intelligence" },
  { Icon: Bot, label: "Agentic AI Workflows" },
];

const caseStudies = [
  {
    company: "Tata AIA Life Insurance",
    title: "AI-Led Risk & Claims Intelligence",
    description:
      "Real-time Early Claims Prediction and Fraud Claims Models integrated into underwriting and claims workflows to identify high-risk policies and suspicious claims proactively.",
    tags: ["Early Claims AI", "Fraud Detection", "Real-Time Scoring"],
    Icon: ShieldCheck,
  },
  {
    company: "SBI Life Insurance",
    title: "Persistency & Fraud Analytics",
    description:
      "Predictive AI models for fraud detection, surrender propensity, lapse revival and persistency prediction to improve retention and reduce revenue leakage across the policy lifecycle.",
    tags: ["Persistency Models", "Lapse Prediction", "Retention AI"],
    Icon: TrendingUp,
  },
  {
    company: "Canara HSBC Life",
    title: "Real-Time Early Risk Detection",
    description:
      "AI-powered Early Claims Prediction integrated at proposal submission stage for retail and group insurance products, enabling proactive underwriting risk controls and fraud prevention.",
    tags: ["Underwriting AI", "Risk Scoring", "Retail + Group"],
    Icon: Zap,
  },
];

const platformFeatures = [
  { Icon: Cpu, label: "Multi-LLM Orchestration" },
  { Icon: Boxes, label: "Hybrid Deployment" },
  { Icon: Lock, label: "Compliance by Design" },
  { Icon: Bot, label: "Agentic AI Workflows" },
];

/* ───────────────────────────── Page ───────────────────────────── */
export default function CaseStudyPage() {
  return (
    <main className="bg-white text-ink" data-testid="case-study-page">
      <SEO
        title="Case Study — Gradatim Intelligence® at Leading Insurers"
        description="How Tata AIA, SBI Life and Canara HSBC use Gradatim Intelligence® to embed AI into claims, fraud detection and persistency operations."
        path="/insights/case-study"
      />
      <Nav />

      {/* SECTION INTRO */}
      <section className="bg-white pt-32 md:pt-40 pb-12 md:pb-16">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 text-center">
          {/* Pill badge */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-copper/20 bg-copper/5"
            data-testid="case-study-badge"
          >
            <Sparkles className="h-3.5 w-3.5 text-copper" strokeWidth={2} />
            <span className="overline text-copper">Gradatim Intelligence</span>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-medium text-4xl sm:text-5xl md:text-[64px] tracking-tight leading-[1.02] text-ink mt-8 max-w-[1000px] mx-auto"
            data-testid="case-study-title"
          >
            AI Models Built for{" "}
            <span className="text-copper">Real Insurance Operations.</span>
          </motion.h1>

          {/* Intro */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="text-base md:text-[18px] text-slate-600 leading-relaxed mt-8 max-w-[780px] mx-auto"
          >
            Gradatim Intelligence combines predictive analytics, machine learning,
            fraud detection, persistency analytics, customer intelligence and
            agentic AI workflows to help insurers improve growth, underwriting,
            claims, retention and operational efficiency.
          </motion.p>

          {/* Feature pills */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.26, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-center justify-center gap-3 mt-12"
          >
            {featurePills.map((p) => (
              <div
                key={p.label}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-hairline hover:border-copper/40 hover:-translate-y-0.5 hover:shadow-sm transition-all"
                data-testid={`feature-pill-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <p.Icon className="h-3.5 w-3.5 text-copper" strokeWidth={1.8} />
                <span className="text-[13px] text-ink font-medium">{p.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CASE STUDY CARDS */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {caseStudies.map((c, i) => (
              <motion.article
                key={c.company}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ delay: i * 0.06, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                className="group bg-white border border-hairline rounded-2xl p-7 md:p-8 hover:-translate-y-1 transition-all duration-300"
                style={{ boxShadow: "0 8px 24px rgba(2,6,23,0.05), 0 1px 2px rgba(2,6,23,0.04)" }}
                data-testid={`case-card-${i}`}
              >
                {/* Icon block */}
                <div className="h-12 w-12 rounded-xl bg-cream border border-hairline group-hover:border-copper group-hover:bg-white flex items-center justify-center mb-7 transition-all">
                  <c.Icon className="h-6 w-6 text-copper" strokeWidth={1.6} />
                </div>

                {/* Company name */}
                <div className="overline text-slate-500 mb-3">{c.company}</div>

                {/* Title */}
                <h3 className="font-display text-[22px] md:text-[24px] tracking-tight leading-tight text-ink mb-4">
                  {c.title}
                </h3>

                {/* Description */}
                <p className="text-[14px] text-slate-600 leading-relaxed mb-6">
                  {c.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 pt-5 border-t border-hairline">
                  {c.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[11px] px-2.5 py-1 rounded-full bg-cream border border-hairline text-slate-700 tracking-wide"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM AI PLATFORM BLOCK */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[24px] md:rounded-[32px] overflow-hidden grain-overlay"
            style={{ background: "#0B1F3A" }}
            data-testid="ai-platform-block"
          >
            {/* glows */}
            <div aria-hidden className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-copper/15 blur-3xl" />
            <div aria-hidden className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.05] pointer-events-none"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
                backgroundSize: "60px 60px",
              }}
            />

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 p-8 md:p-12 lg:p-16">
              {/* LEFT */}
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/15 bg-white/[0.04] mb-7">
                  <span className="h-1.5 w-1.5 bg-copper rounded-full" />
                  <span className="overline text-white/80">Next-Gen AI Platform</span>
                </div>
                <h2 className="font-display font-medium text-3xl md:text-5xl tracking-tight leading-[1.05] text-white mb-6">
                  Secure, Modular & <br />
                  <span className="text-copper">Enterprise-Ready AI</span>{" "}
                  Architecture.
                </h2>
                <p className="text-base md:text-[17px] text-slate-300 leading-relaxed max-w-xl">
                  Gradatim Intelligence combines private and public AI models,
                  agentic workflows, modular deployment architecture and
                  compliance-by-design principles to support scalable AI adoption
                  across insurance operations.
                </p>
              </div>

              {/* RIGHT: 2x2 mini cards */}
              <div className="lg:col-span-5">
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  {platformFeatures.map((f, i) => (
                    <motion.div
                      key={f.label}
                      initial={{ opacity: 0, y: 14 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-30px" }}
                      transition={{ delay: 0.1 + i * 0.08, duration: 0.5 }}
                      className="group bg-white/[0.05] backdrop-blur-md border border-white/15 hover:border-copper/50 hover:bg-white/[0.08] rounded-2xl p-5 md:p-6 transition-all"
                      data-testid={`platform-feature-${i}`}
                    >
                      <div className="h-10 w-10 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center mb-5 group-hover:border-copper transition-colors">
                        <f.Icon className="h-4 w-4 text-copper" strokeWidth={1.7} />
                      </div>
                      <div className="font-display text-[15px] md:text-[16px] tracking-tight text-white leading-snug">
                        {f.label}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-cream border-t border-hairline">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-16 md:py-20 text-center">
          <h2 className="font-display font-medium text-2xl md:text-4xl tracking-tight leading-tight text-ink">
            Explore how Gradatim Intelligence can power{" "}
            <span className="text-copper">your AI roadmap.</span>
          </h2>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="/#contact"
              className="group inline-flex items-center justify-center gap-3 bg-copper hover:bg-copper-600 text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              data-testid="case-final-cta-primary"
            >
              Request a demo
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/products/intelligence"
              className="group inline-flex items-center justify-center gap-3 border-2 border-ink hover:bg-ink hover:text-white text-ink px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              data-testid="case-final-cta-secondary"
            >
              Explore Intelligence
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
